import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  AddOutlined,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import RideApi from "Services/Ride";
import RideWaypointCreateModal from "Pages/Modals/RideWaypoint/Create";
import RideWaypointDeleteModal from "Pages/Modals/RideWaypoint/Delete";

const RideWaypoint = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [addRideWaypointModal, setAddRideWaypointModal] = useState(false);
  const [deleteRideWaypointModal, setDeleteRideWaypointModal] = useState(false);
  const [selectedWaypointId, setSelectedWaypointId] = useState("");

  const normalizeWaypointList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.waypoints)) {
      return { rows: payload.waypoints || [], total: payload?.total || payload?.waypoints?.length || 0 };
    }
    if (Array.isArray(payload?.data?.data)) {
      return { rows: payload.data.data || [], total: payload?.data?.totalRecords || payload?.data?.data?.length || 0 };
    }
    return { rows: [], total: 0 };
  };

  const getWaypoints = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    RideApi.getRideWaypointList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeWaypointList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllWaypoints = useCallback(() => {
    RideApi.getRideWaypointList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => {
        const normalized = normalizeWaypointList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getWaypoints();
    getAllWaypoints();
  }, [getWaypoints, getAllWaypoints]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    waypointId: data?.id || "-",
    rideId: data?.ride_id || data?.rides?.id || "-",
    place: data?.places?.name || "-",
    locality: data?.places?.addressLocality || "-",
    country: data?.places?.addressCountry || "-",
    sequence: data?.sequence_order ?? 0,
    offset: data?.arrival_offset_min ?? 0,
    priceOverride: data?.price_override ?? "0",
    rideStatus: data?.rides?.status || "-",
    departureAt: data?.rides?.departure_at ? new Date(data.rides.departure_at).toLocaleString() : "-",
    raw: data,
  }));

  const formatted = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Waypoint ID": r.waypointId,
    "Ride ID": r.rideId,
    Place: r.place,
    Locality: r.locality,
    Country: r.country,
    Sequence: r.sequence,
    "Arrival Offset (min)": r.offset,
    "Price Override": r.priceOverride,
    "Ride Status": r.rideStatus,
    Departure: r.departureAt,
  }));

  const bulkFormatted = _.orderBy(bulkrow || [], ["created_at"], ["asc"]).map((data, index) => ({
    "S.NO": index + 1,
    "Waypoint ID": data?.id || "-",
    "Ride ID": data?.ride_id || data?.rides?.id || "-",
    Place: data?.places?.name || "-",
    Locality: data?.places?.addressLocality || "-",
    Country: data?.places?.addressCountry || "-",
    Sequence: data?.sequence_order ?? 0,
    "Arrival Offset (min)": data?.arrival_offset_min ?? 0,
    "Price Override": data?.price_override ?? "0",
    "Ride Status": data?.rides?.status || "-",
    Departure: data?.rides?.departure_at ? new Date(data.rides.departure_at).toLocaleString() : "-",
  }));

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleEdit = (waypointRow) => {
    const waypointId = waypointRow?.id || waypointRow?._id;
    if (!waypointId) {
      dispatch(
        showSnackbar({
          message: "Waypoint ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedWaypointId(waypointId);
    setAddRideWaypointModal(true);
  };
  const handleDelete = (waypointRow) => {
    const waypointId = waypointRow?.id || waypointRow?._id;
    if (!waypointId) {
      dispatch(
        showSnackbar({
          message: "Waypoint ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedWaypointId(waypointId);
    setDeleteRideWaypointModal(true);
  };
  const handleAdd = () => setAddRideWaypointModal(true);
  const handleCloseAdd = (created = false) => {
    setAddRideWaypointModal(false);
    setSelectedWaypointId("");
    if (created) {
      getWaypoints();
      getAllWaypoints();
    }
  };
  const handleCloseDelete = (deleted = false) => {
    setDeleteRideWaypointModal(false);
    setSelectedWaypointId("");
    if (deleted) {
      getWaypoints();
      getAllWaypoints();
    }
  };

  const handleCopy = () => {
    const header = Object.keys(formatted[0] || {}).join("\t");
    const rows = formatted.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RideWaypoints");
    XLSX.writeFile(workbook, "RideWaypointList.xlsx");
  };
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "RideWaypointList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [["S.NO", "Waypoint ID", "Ride ID", "Place", "Locality", "Country", "Sequence", "Arrival Offset (min)", "Price Override", "Ride Status", "Departure"]],
      body: formatted.map((r) => Object.values(r)),
    });
    doc.save("RideWaypointList.pdf");
  };
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All RideWaypointList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeStyle = (value) => {
    const key = String(value || "").toLowerCase();
    const map = {
      approved: { bg: "#14b8a6", fg: "#ffffff" },
      active: { bg: "#10b981", fg: "#ffffff" },
      published: { bg: "#10b981", fg: "#ffffff" },
      confirmed: { bg: "#10b981", fg: "#ffffff" },
      completed: { bg: "#0ea5e9", fg: "#ffffff" },
      onboarding: { bg: "#ef4444", fg: "#ffffff" },
      boarding: { bg: "#ef4444", fg: "#ffffff" },
      cancelled: { bg: "#ef4444", fg: "#ffffff" },
      rejected: { bg: "#ef4444", fg: "#ffffff" },
      pending: { bg: "#f59e0b", fg: "#111827" },
      draft: { bg: "#6b7280", fg: "#ffffff" },
      full: { bg: "#7c3aed", fg: "#ffffff" },
      departed: { bg: "#2563eb", fg: "#ffffff" },
      arrived: { bg: "#14b8a6", fg: "#ffffff" },
      inactive: { bg: "#9ca3af", fg: "#111827" },
    };
    return map[key] || { bg: "#64748b", fg: "#ffffff" };
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
   /*  { field: "waypointId", headerName: "Waypoint ID", minWidth: 220, sortable: true },
    { field: "rideId", headerName: "Ride ID", minWidth: 220, sortable: true }, */
    { field: "place", headerName: "Place", minWidth: 180, sortable: true },
    { field: "locality", headerName: "Locality", minWidth: 140, sortable: true },
    { field: "country", headerName: "Country", minWidth: 120, sortable: true },
    { field: "sequence", headerName: "Sequence", minWidth: 100, sortable: true },
    { field: "offset", headerName: "Offset (min)", minWidth: 120, sortable: true },
    { field: "priceOverride", headerName: "Price Override", minWidth: 120, sortable: true },
    {
      field: "rideStatus",
      headerName: "Ride Status",
      minWidth: 130,
      sortable: true,
      renderCell: (params) => {
        const tone = getStatusBadgeStyle(params?.value);
        return <span style={{ background: tone.bg, color: tone.fg, borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", lineHeight: "16px" }}>{String(params?.value || "-")}</span>;
      },
    },
    { field: "departureAt", headerName: "Departure", minWidth: 180, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button variant="contained" color="primary" size="small" style={{ minWidth: 36, padding: "4px 8px" }} onClick={() => handleEdit(params?.row?.raw)}>
            <Edit fontSize="small" />
          </Button>
          <Button variant="contained" color="error" size="small" style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }} onClick={() => handleDelete(params?.row?.raw)}>
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={6} md={6} className={classes.addBtn}>
        <Button variant="contained" color="primary" startIcon={<AddOutlined />} onClick={handleAdd}>
          Add Ride Waypoint
        </Button>
      </Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(1); }}
            onPageChange={(newPage) => { const p = parseInt(newPage, 10) + 1; if (p >= 1) setPage(p); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => setSearchText(model?.quickFilterValues?.join(" ") || "")}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>
      <RideWaypointCreateModal openDialog={addRideWaypointModal} handleClose={handleCloseAdd} waypointId={selectedWaypointId} />
      <RideWaypointDeleteModal openDialog={deleteRideWaypointModal} handleClose={handleCloseDelete} waypointId={selectedWaypointId} />
    </Grid>
  );
};

export default RideWaypoint;
